import React, { useEffect } from "react";
import { ConfirmDialog } from "components/main/utils/ConfirmDialog";
import { MenuGroupLabel, MenuItem } from "components/common/MenuItem";
import { OverlayScroll } from "components/common/OverlayScroll";
import { BarMenu } from "@/components/canvas/overlays/CanvasActionBar/MoreMenu";
import { CATALOG } from "@/actions/catalog";
import { commitSlot } from "@/components/canvas/actionBar/overlays";
import { useBoundStore } from "components/canvas/stores/useAppStore";
import { useFocusCanvasOnClose } from "@/lib/focusCanvas";
import { showNotificationGlobal } from "stores/useNotificationStore";

export interface TypeChangeColumn {
  column_name: string;
  from: string;
  to: string;
}

/** A question a press on Commit raised. */
export interface TypeChangeConfirm {
  view_id: string;
  table_id: string;
  columns: TypeChangeColumn[];
}

const TITLE = "Commit staged edits?";

/** `from` and `to` arrive formatted by `SemanticType`'s `Display`, so each
 *  change reads as the column header prints it ("Number ▶ Text"). */
const Changes = ({ columns }: { columns: readonly TypeChangeColumn[] }) => (
  <>
    <p className="mb-1">
      {columns.length === 1
        ? "Some of your edits change a column's type:"
        : `Some of your edits change ${columns.length} column types:`}
    </p>
    {/* A long list scrolls in place rather than pushing Commit away. */}
    <OverlayScroll className="max-h-32" reveal="hover">
      <ul className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-3">
        {columns.map((c, i) => (
          <li key={i} className="contents">
            <span className="truncate text-fg">
              {c.column_name || "Unnamed column"}
            </span>
            <span className="font-mono text-(length:--font-size-small)">{`${c.from} \u25B6 ${c.to}`}</span>
          </li>
        ))}
      </ul>
    </OverlayScroll>
  </>
);

const close = () => useBoundStore.getState().setTypeChangeConfirm(null);

/// The same question wherever Commit was pressed: hung from the bar's ↑, or a
/// dialog when the bar does not show it. Anything but Commit only dismisses it,
/// leaving the edits staged.
export const TypeChangeConfirmDialog: React.FC = () => {
  const confirm = useBoundStore((state) => state.typeChangeConfirm);
  const clientApp = useBoundStore((state) => state.clientApp);
  const asking = !!confirm && confirm.columns.length > 0;
  const stillAsks = useBoundStore(
    (state) =>
      !confirm || !!state.commitByTable[confirm.table_id]?.typeChangeStaged,
  );

  // A commit discarded or applied elsewhere takes its question with it.
  useEffect(() => {
    if (confirm && !stillAsks) close();
  }, [confirm, stillAsks]);
  useFocusCanvasOnClose(confirm !== null);

  // A conflict leaves the commit staged: say so, as the bar's Commit does.
  const commit = () => {
    try {
      clientApp?.apply_staged_commit(confirm!.view_id);
    } catch (e) {
      showNotificationGlobal(String(e), "error");
    }
    close();
  };

  const anchor = asking ? commitSlot() : null;
  if (asking && anchor)
    return (
      <BarMenu label="Commit" anchor={anchor} onClose={close}>
        <div
          role="group"
          aria-labelledby="type-change-question"
          className="w-72"
        >
          <div id="type-change-question">
            <MenuGroupLabel label={TITLE} />
          </div>
          <div className="px-4 pb-2 text-(length:--font-size) text-fg-muted">
            <Changes columns={confirm.columns} />
          </div>
          <MenuItem
            Icon={CATALOG.commit.Icon}
            label="Commit"
            className="hover:text-commit-ready-active focus-visible:text-commit-ready-active"
            onClick={commit}
          />
        </div>
      </BarMenu>
    );

  return (
    <ConfirmDialog
      open={asking}
      title={TITLE}
      message={asking ? <Changes columns={confirm.columns} /> : ""}
      confirmText="Commit"
      onConfirm={commit}
      onCancel={close}
    />
  );
};
